import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { KeyboardEventHandler, ReactElement, RefObject } from 'react';
import { useRef, useEffect, useState } from 'react';
import type { TextDropdownProps } from './TextDropdown.data';
import styles from './TextDropdown.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './TextDropdown.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';

import DynamicComponent from '../../DynamicComponent';

import RichText from '../rich-text/RichText';
import type { RichTextBlokData } from '../rich-text/RichText.data';
import type { StoryblokComponent } from 'storyblok-js-client';
import Icon from '../../atoms/icon/Icon';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';

export default function TextDropdown({
  isVisible,
  className,
  blok,
  ref,
}: TextDropdownProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const iconRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = useState(false);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  const onClick = () => {
    setOpen((o) => !o);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      onClick();
    }
  };

  useEffect(() => {
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        // @ts-ignore

        scaleY: isOpen ? -1 : 1,
        duration: 0.4,
      });
    }

    gsap.to(contentRef.current, {
      height: isOpen ? contentRef.current?.scrollHeight : 0,
      paddingBottom: isOpen ? undefined : 0,
      // duration: immediate ? 0 : 0.6,
      ease: 'Quad.easeInOut',
      clearProps: isOpen ? 'height,paddingBottom' : '',
      onComplete: () => {
        ScrollTrigger.refresh();
      },
    });
  }, [isOpen]);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.textDropdown, className)}
      {...sbEditable(blok)}
    >
      <div
        onKeyDown={handleKeyDown}
        role="button"
        onClick={onClick}
        tabIndex={0}
        className={classNames(styles.header)}
      >
        <div className={classNames(styles.headerContent)}>
          <RichText
            blok={{ text: blok.title } as RichTextBlokData & StoryblokComponent<string>}
            className={styles.title}
          />
          <Icon name="chevron-down" className={classNames(styles.icon)} ref={iconRef} />
        </div>
      </div>

      <div ref={contentRef} className={styles.content}>
        {blok.modules.map((module) => (
          <DynamicComponent
            key={module._uid}
            blok={module}
            // canTransitionIn={canTransitionIn}
            className={classNames({
              [styles.childRichText]: module.component === 'rich_text',
              [styles.childDropdown]: module.component === 'text_dropdown',
            })}
          />
        ))}
      </div>

      <div ref={lineRef} className={styles.listLine} />
    </div>
  );
}
